import type {
  MissingExchangeRateRequirement,
  PayrollCalculateErrorResponse,
  PayrollFxRequirements,
  PreflightCheck,
  PreflightResult,
} from '@/features/payroll/types/payroll.types'

function isMissingExchangeRateRequirement(
  value: unknown,
): value is MissingExchangeRateRequirement {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as Record<string, unknown>
  return (
    typeof candidate.fromCurrency === 'string' &&
    typeof candidate.toCurrency === 'string' &&
    typeof candidate.asOf === 'string' &&
    Array.isArray(candidate.affectedEmployeeIds) &&
    candidate.affectedEmployeeIds.every((id) => typeof id === 'string')
  )
}

function toMissingExchangeRateArray(
  value: unknown,
): MissingExchangeRateRequirement[] {
  if (!Array.isArray(value)) return []
  return value.filter(isMissingExchangeRateRequirement)
}

function normalizeFxRequirements(value: unknown): PayrollFxRequirements | null {
  if (typeof value !== 'object' || value === null) return null
  const candidate = value as Record<string, unknown>
  const missing = toMissingExchangeRateArray(candidate.missing)
  if (
    typeof candidate.baseCurrency !== 'string' ||
    typeof candidate.asOf !== 'string' ||
    missing.length === 0
  ) {
    return null
  }

  return {
    baseCurrency: candidate.baseCurrency,
    asOf: candidate.asOf,
    missing,
  }
}

function getMissingExchangeRateBlock(
  checks: PreflightCheck[],
): PayrollFxRequirements | null {
  const block = checks.find((check) => check.code === 'MISSING_EXCHANGE_RATES')
  return normalizeFxRequirements(block?.details)
}

export function getFxRequirementsFromPreflight(
  preflight: PreflightResult | undefined,
): PayrollFxRequirements | null {
  if (!preflight) return null
  return (
    normalizeFxRequirements(preflight.fxRequirements) ??
    getMissingExchangeRateBlock(preflight.hardBlocks)
  )
}

export function getFxRequirementsFromCalculateError(
  error: unknown,
  fallbackBaseCurrency?: string,
  fallbackAsOf?: string,
): PayrollFxRequirements | null {
  const payload = (error as { response?: { data?: PayrollCalculateErrorResponse } })?.response?.data
  const missing = toMissingExchangeRateArray(payload?.missing)
  if (missing.length === 0) return null

  return {
    baseCurrency: fallbackBaseCurrency ?? missing[0]?.toCurrency ?? '',
    asOf: fallbackAsOf ?? missing[0]?.asOf ?? '',
    missing,
  }
}
